import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';
import { createDefaultWatermark } from '../../../helper/standardChart';

const visInfo: Layer['visInfo'] = {
  id: 'dynamicLine',
  name_en: 'Dynamic Line',
  name_zh: '动态折线图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    data: amount => amount > 1,
    x: amount => amount == 1,
    y: amount => amount == 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.DATE],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    z: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    z?: string;
  };
  dvShowWaterMark?: boolean;
}

export const dynamicLine = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  withTimeline: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook } = api;
    const chartViewRect = $dom.getBoundingClientRect();
    const {
      dataIndex = 0,
      encoding: { x, y, z },
      dvShowWaterMark = false
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);

    // * ------------------------- data
    const { dates, zNameData = [y] } = util.dataEncoding.encoding(data.values.slice(), {
      dates: {
        field: x,
        transforms: [{ type: 'sort', order: 'ascending' }, { type: 'deDuplication' }]
      },
      zNameData: {
        field: z!,
        transforms: [
          { type: 'deDuplication' },
          { type: 'filter', cb: val => typeof val !== 'undefined' }
        ]
      }
    });

    // * ------------------------- chart
    const graphics = dates.map(date => {
      return createDefaultWatermark(chartViewRect, date);
    });
    const grid = {
      // ! bugs
      dvBoxLayout: null
    };
    const seriesData = zNameData.map(zItem => {
      return {
        name: zItem,
        type: 'line',
        yAxisIndex: 0,
        xAxisIndex: 0,
        __encodeField: zItem,
        stateAnimation: {
          duration: 0
        },
        itemStyle: {
          opacity: 0
        },
        label: {
          show: false
        },
        endLabel: {
          show: true,
          valueAnimation: true,
          formatter(params) {
            const result = hook.formatDataNumberValue(params.value, {
              type: 'table',
              unit: dataMetaInfo[y]?.unit
            });
            return `{name|${`${params.seriesName}  `}}{value|${result.value + result.unit}}`;
          },
          rich: {
            name: {
              color: 'inherit',
              fontSize: 10,
              verticalAlign: 'middle',
              fontWeight: 'bold'
            },
            value: {
              color: 'rgba(0,0,0,0.84)',
              fontSize: 10,
              align: 'right',
              verticalAlign: 'middle',
              fontWeight: 500
            }
          }
        },
        data: util.dataTransform.transform(
          dates.map(
            date =>
              data.values.find(
                dataItem =>
                  dataItem[x] === date && (typeof z === 'undefined' || dataItem[z] === zItem)
              )?.[y]
          ) as number[],
          [{ type: 'toNumeric' }]
        )
      };
    });
    let option = {
      grid,
      xAxis: [
        {
          show: true,
          __encodeField: x,
          type: 'category',
          name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
          data: dates
        }
      ],
      yAxis: [
        {
          show: true,
          __encodeField: y,
          dvSplitLineNumber: hook.getSplitLineNumber(chartViewRect.height - grid.top - grid.bottom),
          type: 'value',
          name: y + (dataMetaInfo[y]?.unit ? `(${dataMetaInfo[y].unit})` : '')
        }
      ],
      graphic: dvShowWaterMark ? graphics[graphics.length - 1] : undefined,
      series: seriesData,
      animation: true
    };

    return renderApi => {
      option = renderApi.mergeChartOptionProxy(option);

      const timelineIns = renderApi.renderTimelineProxy(dates as string[]);
      renderApi.renderLegendProxy(option.legend.data);
      const chartIns = renderApi.renderChartProxy(
        {
          option
        },
        { theme: 'pc-app-light' }
      );

      const dateIndex = dates.length - 1;
      let timelineHelper;
      chartIns.on('dv:afterupdate', () => {
        timelineHelper = new ThsDataVStandardChart.helper.LineTimeLineHelper(chartIns, {
          verticalPointer: {
            style: {
              stroke:
                (Array.isArray(option.xAxis) ? option.xAxis[0] : option.xAxis)?.axisLine?.lineStyle
                  ?.color || '#333'
            }
          },
          // 传参 更新时间间隔
          duration: option?.animationDuration || 1000
        });

        timelineHelper.change(dateIndex);
      });
      timelineIns?.on?.('change', e => {
        const { index } = e;
        if (dvShowWaterMark) {
          const ecIns = chartIns.getECharts();
          const ecModel = ecIns.getModel();
          const graphicModel = ecModel.getComponent('graphic');
          const graphicView = ecIns.getViewOfComponentModel(graphicModel);

          graphicModel.optionUpdated(graphics[index], false);

          graphicView.render(graphicModel, ecModel, ecIns);
        }

        timelineHelper.change(index);
      });
    };
  }
});
